import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { apiService, applyAuthSession } from '../services';
import {
  clearStoredToken,
  getStoredRefreshToken,
  getStoredToken,
} from '../auth';
import type { AuthContextType, AuthUser } from '../types';
import { toAuthUser } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Shared in-flight session restore so React Strict Mode does not double-call /auth/me. */
let sessionRestorePromise: Promise<AuthUser | null> | null = null;

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token = getStoredToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    if (!sessionRestorePromise) {
      sessionRestorePromise = apiService
        .me()
        .then((response) => {
          if (response.success && response.data) {
            return toAuthUser(response.data);
          }
          clearStoredToken();
          return null;
        })
        .catch(() => {
          clearStoredToken();
          return null;
        })
        .finally(() => {
          sessionRestorePromise = null;
        });
    }

    sessionRestorePromise
      .then((restoredUser) => {
        if (cancelled) return;
        setUser(restoredUser);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const loginWithEmail = useCallback(async (email: string, password: string): Promise<AuthUser> => {
    const response = await apiService.login(email, password);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Sign-in failed. Please try again.');
    }
    const nextUser = applyAuthSession(response.data);
    setUser(nextUser);
    return nextUser;
  }, []);

  const completeOAuth = useCallback(async (code: string): Promise<AuthUser> => {
    const response = await apiService.oauthCallback(code);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Sign-in failed. Please try again.');
    }
    const nextUser = applyAuthSession(response.data);
    setUser(nextUser);
    return nextUser;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const refreshToken = getStoredRefreshToken();
    const accessToken = getStoredToken();

    clearStoredToken();
    setUser(null);

    if (refreshToken && accessToken) {
      try {
        await apiService.logout(accessToken, refreshToken);
      } catch {
        // Local session is already cleared.
      }
    }
  }, []);

  const isSuperadmin = useMemo(() => user?.role === 'leadership', [user?.role]);
  const canMonitor = useMemo(
    () => user?.role === 'internal_monitor' || user?.role === 'leadership',
    [user?.role]
  );

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isSuperadmin,
    canMonitor,
    loginWithEmail,
    completeOAuth,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
